import React, { useEffect, useRef, useState } from 'react';
import { 
  XMarkIcon, 
  ArrowRightIcon, 
  ArrowLeftIcon,
  PlayIcon 
} from '@heroicons/react/24/outline';
import { useNewTour } from '../contexts/NewTourContext';

interface TooltipPosition {
  x: number;
  y: number;
  arrowPosition: string;
}

const NewTourOverlay: React.FC = () => {
  const {
    isActive,
    isVisible,
    currentStep,
    totalSteps,
    currentStepData,
    nextStep,
    prevStep,
    skipTour,
    handleTargetClick
  } = useNewTour();

  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({ x: 0, y: 0, arrowPosition: '' });

  // Calculate tooltip position
  const calculatePosition = (): TooltipPosition => {
    if (!currentStepData || !tooltipRef.current) {
      return { x: 0, y: 0, arrowPosition: '' };
    }

    const targetElement = document.querySelector(`[data-tour="${currentStepData.target}"]`);
    if (!targetElement) {
      console.log(`🎯 Tour: Target element ${currentStepData.target} not found`);
      return { x: 50, y: 50, arrowPosition: '' };
    }

    const targetRect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    
    console.log(`🎯 Tour: Target rect:`, targetRect);
    console.log(`🎯 Tour: Tooltip rect:`, tooltipRect);

    const TOOLTIP_WIDTH = 320;
    const TOOLTIP_HEIGHT = tooltipRect.height || 250;
    const SPACING = 20;

    let x = 0;
    let y = 0;
    let arrowPosition = '';

    // Calculate position based on preferred position
    switch (currentStepData.position) {
      case 'top':
        x = targetRect.left + (targetRect.width - TOOLTIP_WIDTH) / 2;
        y = targetRect.top - TOOLTIP_HEIGHT - SPACING;
        arrowPosition = 'bottom-center';
        break;
      case 'bottom':
        x = targetRect.left + (targetRect.width - TOOLTIP_WIDTH) / 2;
        y = targetRect.bottom + SPACING;
        arrowPosition = 'top-center';
        break;
      case 'left':
        x = targetRect.left - TOOLTIP_WIDTH - SPACING;
        y = targetRect.top + (targetRect.height - TOOLTIP_HEIGHT) / 2;
        arrowPosition = 'right-center';
        break;
      case 'right':
        x = targetRect.right + SPACING;
        y = targetRect.top + (targetRect.height - TOOLTIP_HEIGHT) / 2;
        arrowPosition = 'left-center';
        break;
    }

    // Adjust for viewport boundaries
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 20;

    // Horizontal adjustments
    if (x < margin) {
      x = margin;
    } else if (x + TOOLTIP_WIDTH > viewportWidth - margin) {
      x = viewportWidth - TOOLTIP_WIDTH - margin;
    }

    // Vertical adjustments
    if (y < margin) {
      y = margin;
    } else if (y + TOOLTIP_HEIGHT > viewportHeight - margin) {
      y = viewportHeight - TOOLTIP_HEIGHT - margin;
    }

    console.log(`🎯 Tour: Calculated position:`, { x, y, arrowPosition });

    return { x, y, arrowPosition };
  };

  // Position tooltip and add spotlight
  useEffect(() => {
    if (!isActive || !isVisible || !currentStepData) return;

    // If waitForElement is true, wait for the target element to be available
    if (currentStepData.waitForElement) {
      console.log(`🎯 Tour: Waiting for element ${currentStepData.target}...`);
      
      // Hide the overlay initially while waiting
      setTooltipPosition({ x: -9999, y: -9999, arrowPosition: '' });
      
      const checkForElement = () => {
        const targetElement = document.querySelector(`[data-tour="${currentStepData.target}"]`);
        if (!targetElement) {
          console.log(`🎯 Tour: Element ${currentStepData.target} not found, checking again...`);
          setTimeout(checkForElement, 300); // Check every 300ms for faster response
          return;
        }
        
        // Additional check: ensure the element is actually visible and in the DOM
        const rect = targetElement.getBoundingClientRect();
        if (rect.width === 0 && rect.height === 0) {
          console.log(`🎯 Tour: Element ${currentStepData.target} found but not visible, waiting...`);
          setTimeout(checkForElement, 300);
          return;
        }
        
        console.log(`🎯 Tour: Element ${currentStepData.target} found and visible, positioning tour`);
        // Small delay to ensure page is fully rendered
        setTimeout(() => {
          positionTour();
        }, 500);
      };
      
      // Start checking for element
      checkForElement();
      return;
    }

    // If not waiting for element, position immediately
    positionTour();

    function positionTour() {
      if (!currentStepData) return;
      
      const updatePosition = () => {
        if (!currentStepData) return;
        
        const position = calculatePosition();
        setTooltipPosition(position);

        // Add spotlight effect
        const targetElement = document.querySelector(`[data-tour="${currentStepData.target}"]`);
        if (targetElement && currentStepData.spotlight) {
          // Remove existing spotlights
          document.querySelectorAll('.tour-spotlight').forEach(el => {
            el.classList.remove('tour-spotlight');
          });
          targetElement.classList.add('tour-spotlight');
          
          // Scroll target into view if needed
          const rect = targetElement.getBoundingClientRect();
          const isVisible = rect.top >= 0 && rect.left >= 0 && 
                           rect.bottom <= window.innerHeight && rect.right <= window.innerWidth;
          
          if (!isVisible) {
            targetElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center'
            });
            // Recalculate position after scroll
            setTimeout(updatePosition, 300);
          }
        }
      };

      // Initial positioning
      const timer = setTimeout(updatePosition, 100);

      // Reposition on scroll and resize
      const handleReposition = () => {
        updatePosition();
      };

      window.addEventListener('scroll', handleReposition, { passive: true });
      window.addEventListener('resize', handleReposition, { passive: true });

      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', handleReposition);
        window.removeEventListener('resize', handleReposition);
        
        // Clean up spotlight
        document.querySelectorAll('.tour-spotlight').forEach(el => {
          el.classList.remove('tour-spotlight');
        });
      };
    }
  }, [isActive, isVisible, currentStepData]);

  // Global click handler for tour targets (excluding navigation elements)
  useEffect(() => {
    if (!isActive || !currentStepData || currentStepData.action !== 'click') return;

    // Skip global click handling for all navigation elements since they're handled in EnhancedLayout
    if (currentStepData.target.includes('-nav')) return;

    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const tourElement = target.closest(`[data-tour="${currentStepData.target}"]`);
      
      if (tourElement) {
        console.log(`🎯 Tour: Global click detected on ${currentStepData.target}`);
        event.preventDefault();
        event.stopPropagation();
        
        // Use handleTargetClick instead of nextStep directly to handle special cases
        const tourId = currentStepData.target;
        handleTargetClick(tourId);
      }
    };

    // Add click listener to document
    document.addEventListener('click', handleGlobalClick, true);

    return () => {
      document.removeEventListener('click', handleGlobalClick, true);
    };
  }, [isActive, currentStepData, handleTargetClick]);

  if (!isActive || !isVisible || !currentStepData) {
    return null;
  }

  return (
    <>
      {/* Dark overlay - enhanced with better blur and gradient */}
      {!currentStepData.spotlight && (
        <div 
          className="fixed inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/70 z-40 pointer-events-none animate-fadeIn"
          style={{ backdropFilter: 'blur(4px)' }}
        />
      )}
      
      {/* Tour tooltip - enhanced design */}
      <div
        ref={tooltipRef}
        className="fixed z-[60] pointer-events-auto animate-scaleIn"
        style={{
          left: `${tooltipPosition.x}px`,
          top: `${tooltipPosition.y}px`,
          width: '360px'
        }}
      >
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden transform hover:scale-105 transition-all duration-300">
          {/* Header - enhanced gradient */}
          <div className="bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 px-6 py-4 text-white relative overflow-hidden">
            {/* Animated background pattern */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 animate-morph" />
            
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <PlayIcon className="h-4 w-4 animate-pulse" />
                </div>
                <span className="font-bold text-lg">
                  Step {currentStep + 1} of {totalSteps}
                </span>
              </div>
              <button
                onClick={skipTour}
                className="text-white/80 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/20 backdrop-blur-sm"
                aria-label="Skip tour"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            {/* Enhanced progress bar */}
            <div className="mt-3 w-full bg-white/20 rounded-full h-2 overflow-hidden backdrop-blur-sm">
              <div 
                className="h-full bg-gradient-to-r from-white to-yellow-200 rounded-full transition-all duration-700 ease-out shadow-lg"
                style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>
          
          {/* Content - enhanced layout */}
          <div className="p-6 space-y-6">
            <div className="space-y-3">
              <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                {currentStepData.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {currentStepData.content}
              </p>
            </div>
            
            {/* Enhanced action hints */}
            {currentStepData.action === 'click' && (
              <div className="flex items-center space-x-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                  Click the highlighted area to continue
                </span>
              </div>
            )}
            
            {currentStepData.action === 'next' && (
              <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-bounce" />
                <span className="text-sm font-medium text-green-700 dark:text-green-300">
                  Click "Next" when you're ready to continue
                </span>
              </div>
            )}
            
            {currentStepData.action === 'wait' && (
              <div className="flex items-center space-x-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
                  Automatically continuing...
                </span>
              </div>
            )}
            
            {/* Enhanced navigation buttons */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                <span className="font-medium">Back</span>
              </button>
              
              <div className="flex space-x-3">
                <button
                  onClick={skipTour}
                  className="px-4 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 font-medium"
                >
                  Skip Tour
                </button>
                
                {/* Enhanced Next button for non-click actions */}
                {(currentStepData.action !== 'click') && (
                  <button
                    onClick={nextStep}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-500 via-purple-600 to-pink-500 hover:from-blue-600 hover:via-purple-700 hover:to-pink-600 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl font-medium"
                  >
                    <span>{currentStep === totalSteps - 1 ? 'Finish' : 'Next'}</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Enhanced arrow pointer */}
        {tooltipPosition.arrowPosition && (
          <div 
            className={`absolute w-4 h-4 bg-white/95 dark:bg-gray-900/95 border border-gray-200/50 dark:border-gray-700/50 rotate-45 shadow-lg ${
              tooltipPosition.arrowPosition === 'bottom-center' ? 'bottom-[-8px] left-1/2 transform -translate-x-1/2' :
              tooltipPosition.arrowPosition === 'top-center' ? 'top-[-8px] left-1/2 transform -translate-x-1/2' :
              tooltipPosition.arrowPosition === 'right-center' ? 'right-[-8px] top-1/2 transform -translate-y-1/2' :
              tooltipPosition.arrowPosition === 'left-center' ? 'left-[-8px] top-1/2 transform -translate-y-1/2' :
              ''
            }`}
          />
        )}
      </div>
    </>
  );
};

export default NewTourOverlay;
